import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button } from '@mono/ui-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DollarSign, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { pricingApiConnect } from '../../lib/api/pricingApi.connect';
import { PricingWorkspace } from './PricingWorkspace/PricingWorkspace';
export function PricingTab({ projectId }) {
    const queryClient = useQueryClient();
    const [isCreating, setIsCreating] = useState(false);
    const [scenarioName, setScenarioName] = useState('');
    const [selectedScenario, setSelectedScenario] = useState(null);
    const { data, isLoading, error } = useQuery({
        queryKey: [
            'pricing',
            projectId
        ],
        queryFn: ()=>pricingApiConnect.listPricing(projectId),
        staleTime: 30000
    });
    const createMutation = useMutation({
        mutationFn: (body)=>pricingApiConnect.createPricing(projectId, body),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'pricing',
                    projectId
                ]
            });
            setIsCreating(false);
            setScenarioName('');
        }
    });
    const deleteMutation = useMutation({
        mutationFn: (pricingId)=>pricingApiConnect.deletePricing(projectId, pricingId),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'pricing',
                    projectId
                ]
            });
        }
    });
    const handleCreate = ()=>{
        if (!scenarioName.trim()) return;
        createMutation.mutate({
            scenarioName: scenarioName.trim(),
            seedFromExtractions: true
        });
    };
    // Workspace mode — show full pricing editor
    if (selectedScenario) {
        return /*#__PURE__*/ _jsx(PricingWorkspace, {
            projectId: projectId,
            pricingId: selectedScenario.id,
            scenarioName: selectedScenario.name,
            onBack: ()=>setSelectedScenario(null)
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsx("div", {
                className: "text-gray-500",
                children: "Loading pricing scenarios..."
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsx("div", {
                className: "text-red-500",
                children: "Failed to load pricing scenarios"
            })
        });
    }
    const scenarios = data?.scenarios ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-lg font-semibold",
                                children: "Pricing Scenarios"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-gray-500 mt-1",
                                children: "Create and manage pricing scenarios for this proposal"
                            })
                        ]
                    }),
                    !isCreating && /*#__PURE__*/ _jsxs(Button, {
                        size: "sm",
                        onClick: ()=>setIsCreating(true),
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "w-4 h-4 mr-2"
                            }),
                            "New Scenario"
                        ]
                    })
                ]
            }),
            isCreating && /*#__PURE__*/ _jsx("div", {
                className: "border border-gray-200 rounded-lg p-4 bg-gray-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-end gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    htmlFor: "scenario-name",
                                    className: "block text-sm font-medium text-gray-700 mb-1",
                                    children: "Scenario Name"
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    id: "scenario-name",
                                    type: "text",
                                    value: scenarioName,
                                    onChange: (e)=>setScenarioName(e.target.value),
                                    placeholder: "e.g., Competitive Bid, Best Value",
                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                                    onKeyDown: (e)=>{
                                        if (e.key === 'Enter') handleCreate();
                                        if (e.key === 'Escape') {
                                            setIsCreating(false);
                                            setScenarioName('');
                                        }
                                    }
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-gray-400 mt-1",
                                    children: "Will auto-seed labor categories and CLINs from extracted data"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            size: "sm",
                            onClick: handleCreate,
                            disabled: !scenarioName.trim() || createMutation.isPending,
                            children: createMutation.isPending ? 'Creating...' : 'Create'
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            size: "sm",
                            variant: "outline",
                            onClick: ()=>{
                                setIsCreating(false);
                                setScenarioName('');
                            },
                            children: "Cancel"
                        })
                    ]
                })
            }),
            scenarios.length === 0 && !isCreating ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center justify-center py-16 text-center",
                children: [
                    /*#__PURE__*/ _jsx(DollarSign, {
                        className: "w-12 h-12 text-gray-300 mb-4"
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-medium text-gray-600 mb-2",
                        children: "No pricing scenarios yet"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-400 max-w-md",
                        children: "Create a pricing scenario to start building your price volume. Labor categories and CLINs will be automatically seeded from your extraction data."
                    })
                ]
            }) : /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                children: scenarios.map((scenario)=>/*#__PURE__*/ _jsx("div", {
                        className: "border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer",
                        onClick: ()=>setSelectedScenario({
                                id: scenario.id,
                                name: scenario.scenarioName
                            }),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(DollarSign, {
                                            className: "w-5 h-5 text-gray-400"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium text-gray-900",
                                                            children: scenario.scenarioName
                                                        }),
                                                        scenario.isPrimary && /*#__PURE__*/ _jsxs(Badge, {
                                                            variant: "secondary",
                                                            className: "bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Star, {
                                                                    className: "w-3 h-3 mr-0.5 inline"
                                                                }),
                                                                "Primary"
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("p", {
                                                    className: "text-xs text-gray-400 mt-0.5",
                                                    children: [
                                                        "Created",
                                                        ' ',
                                                        new Date(scenario.createdAt).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex items-center gap-2",
                                    children: /*#__PURE__*/ _jsx(Button, {
                                        size: "sm",
                                        variant: "ghost",
                                        className: "text-red-500 hover:text-red-700 hover:bg-red-50",
                                        onClick: (e)=>{
                                            e.stopPropagation();
                                            deleteMutation.mutate(scenario.id);
                                        },
                                        disabled: deleteMutation.isPending,
                                        children: /*#__PURE__*/ _jsx(Trash2, {
                                            className: "w-4 h-4"
                                        })
                                    })
                                })
                            ]
                        })
                    }, scenario.id))
            })
        ]
    });
}
